import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, BookOpen, Users, Star, TrendingUp, Eye, Edit, Copy, Trash2, BookMarked, Loader, AlertTriangle } from 'lucide-react';
import './CourseListTab.css';
import {
  getActiveCourses,
  getPendingCourses,
  deleteCourse,
  type CourseListItem,
} from '../../../services/AdminService/AdminCourseManagementService';
import CoursePendingHint from '../../../Components/CourseManagement/CoursePendingHint';
import { mapApiCourseStatus, type CourseDisplayStatus } from '../../../Components/CourseManagement/courseStatus';
import { getApprovalConfig } from '../../../services/PagesServices/CourseApprovalService';

interface Course {
  id: number;
  title: string;
  category: string;
  description: string;
  instructor: string;
  students: number;
  modules: number;
  rating: number;
  completion: number;
  status: CourseDisplayStatus;
  updatedAt: string;
  thumbnail?: string;
  level?: string;
}

// Approver info for a pending course, keyed by course id (Super Admin can read
// the approval config; used to tell the creator who must approve next).
interface ApproverInfo {
  approverName?: string | null;
  approverEmail?: string | null;
}

interface CourseListTabProps {
  onEditCourse?: (courseId: number) => void;
}

const CourseListTab: React.FC<CourseListTabProps> = ({ onEditCourse }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [courses, setCourses] = useState<Course[]>([]);
  const [approvers, setApprovers] = useState<Record<number, ApproverInfo>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load courses on mount
  useEffect(() => {
    loadCourses();
  }, [selectedStatus]);

  const loadCourses = async () => {
    setLoading(true);
    setError(null);

    try {
      let result: CourseListItem[] = [];

      if (selectedStatus === 'all') {
        // Load both active and pending courses
        const [activeRes, pendingRes] = await Promise.all([
          getActiveCourses(),
          getPendingCourses(),
        ]);

        const activeCourses = activeRes.data || [];
        const pendingCourses = pendingRes.data || [];

        result = [...activeCourses, ...pendingCourses];
      } else if (selectedStatus === 'active' || selectedStatus === 'published') {
        const res = await getActiveCourses();
        result = res.data || [];
      } else if (selectedStatus === 'draft' || selectedStatus === 'pending') {
        const res = await getPendingCourses();
        result = res.data || [];
      }

      // Transform API data to component format
      const transformedCourses: Course[] = result.map((course) => ({
        id: course.id,
        title: course.title,
        category: 'General', // You can add category mapping if needed
        description: course.shortDescription || 'No description available',
        instructor: 'Instructor', // Can be fetched from course creator if needed
        students: course.enrollmentCount || 0,
        modules: course.lessonCount || 0,
        rating: 0, // Can be calculated from reviews if available
        completion: 0, // Can be calculated from enrollments
        status: mapApiCourseStatus(course.status),
        updatedAt: formatDate(course.updatedAt || course.createdAt),
        thumbnail: course.thumbnail,
        level: course.level,
      }));

      setCourses(transformedCourses);
      loadApprovers(transformedCourses);
    } catch (err: any) {
      console.error('Failed to load courses:', err);
      setError('Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // For pending courses, fetch who the designated approver is so the card hint
  // can name them. Super Admin is the only role allowed to read approval config.
  const loadApprovers = async (list: Course[]) => {
    const pending = list.filter((c) => c.status === 'Pending');
    if (pending.length === 0) {
      setApprovers({});
      return;
    }
    try {
      const entries = await Promise.all(
        pending.map(async (c) => {
          const res = await getApprovalConfig(c.id);
          const cfg = res.success ? res.data : undefined;
          return [c.id, { approverName: cfg?.approverName, approverEmail: cfg?.approverEmail }] as const;
        })
      );
      setApprovers(Object.fromEntries(entries));
    } catch (err) {
      // Non-fatal: the hint just falls back to its generic wording.
      console.error('Failed to load approver info:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const handleDelete = async (courseId: number) => {
    if (!window.confirm('Are you sure you want to delete this course?')) {
      return;
    }

    try {
      const result = await deleteCourse(courseId);
      if (result.success) {
        // Reload courses after deletion
        await loadCourses();
      } else {
        alert(result.error?.message || 'Failed to delete course');
      }
    } catch (err: any) {
      alert('Failed to delete course: ' + err.message);
    }
  };

  const handleEdit = (courseId: number) => {
    // Call the parent handler to switch to edit mode
    if (onEditCourse) {
      onEditCourse(courseId);
    } else {
      console.log('Edit course:', courseId);
      alert(`Edit functionality for course ${courseId} is not available.`);
    }
  };

  const handleView = (courseId: number) => {
    // Course view is shared with Admin (route widened to Super Admin). Pass the
    // Super Admin course-management path so the breadcrumb "back" returns here.
    navigate(`/admin/course/${courseId}`, {
      state: { sourceLabel: 'Course Management', backTo: '/superadmin/content/course-management' }
    });
  };

  const handleCopy = async (courseId: number) => {
    // TODO: Implement course duplication
    console.log('Copy course:', courseId);
    alert(`Copy functionality for course ${courseId} will be implemented`);
  };

  // Filter courses based on search and category
  const filteredCourses = courses.filter((course) => {
    const matchesSearch =
      searchQuery === '' ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === '' ||
      course.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Calculate stats from loaded courses
  const totalCourses = courses.length;
  const activeCourses = courses.filter(c => c.status === 'Active').length;
  const totalStudents = courses.reduce((sum, c) => sum + c.students, 0);
  const avgRating = courses.length > 0
    ? (courses.reduce((sum, c) => sum + c.rating, 0) / courses.length).toFixed(1)
    : '0.0';
  const avgCompletion = courses.length > 0
    ? Math.round(courses.reduce((sum, c) => sum + c.completion, 0) / courses.length)
    : 0;

  const stats = [
    { label: 'Total Courses', value: totalCourses.toString(), change: `${activeCourses} active`, icon: BookOpen, color: '#FF8811' },
    { label: 'Total Learners', value: totalStudents.toString(), change: 'All courses', icon: Users, color: '#00A045' },
    { label: 'Avg. Rating', value: avgRating, change: 'Based on reviews', icon: Star, color: '#FACC15' },
    { label: 'Avg. Completion', value: `${avgCompletion}%`, change: 'All courses', icon: TrendingUp, color: '#8200DB' }
  ];

  return (
    <>
      {/* Error Message */}
      {error && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          backgroundColor: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <AlertTriangle size={20} color="#dc2626" />
          <span style={{ color: '#dc2626' }}>{error}</span>
        </div>
      )}

      <div className="trainer-coursemanagement-stats-grid">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="trainer-coursemanagement-stat-card">
              <div className="trainer-coursemanagement-stat-content">
                <div className="trainer-coursemanagement-stat-label">{stat.label}</div>
                <div className="trainer-coursemanagement-stat-value">{stat.value}</div>
                <div className="trainer-coursemanagement-stat-change">{stat.change}</div>
              </div>
              <div className="trainer-coursemanagement-stat-icon" style={{ backgroundColor: stat.color }}>
                <IconComponent size={24} color="white" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="trainer-coursemanagement-filters">
        <div className="trainer-coursemanagement-search-box">
          <Search className="trainer-coursemanagement-search-icon" size={20} />
          <input
            type="text"
            placeholder="Search courses by title, instructor, or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="trainer-coursemanagement-filter-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Categories</option>
          <option value="Leadership">Leadership</option>
          <option value="Technical">Technical</option>
          <option value="Management">Management</option>
        </select>
        <select
          className="trainer-coursemanagement-filter-select"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="active">Active/Published</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem',
          flexDirection: 'column',
          gap: '1rem',
        }}>
          <Loader size={48} className="spinner" color="#FF8C00" />
          <p style={{ color: '#6b7280' }}>Loading courses...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredCourses.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '4rem',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db',
        }}>
          <BookOpen size={64} color="#9ca3af" style={{ margin: '0 auto 1rem' }} />
          <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', color: '#374151' }}>
            No courses found
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
            {searchQuery || selectedCategory
              ? 'Try adjusting your filters'
              : 'Create your first course to get started'}
          </p>
        </div>
      )}

      {/* Courses Grid */}
      {!loading && filteredCourses.length > 0 && (
        <div className="trainer-coursemanagement-courses-grid">
          {filteredCourses.map((course) => (
          <div key={course.id} className="trainer-coursemanagement-course-card">
            <div className="trainer-coursemanagement-course-card-header">
              <div className="trainer-coursemanagement-course-icon-wrapper">
                <BookMarked size={48} strokeWidth={1.5} color="#9ca3af" />
              </div>
              <span className={`trainer-coursemanagement-status-badge trainer-coursemanagement-status-${course.status.toLowerCase()}`}>
                {course.status}
              </span>
            </div>

            <h3 className="trainer-coursemanagement-course-title">{course.title}</h3>

            <span className={`trainer-coursemanagement-category-badge trainer-coursemanagement-category-${course.category.toLowerCase()}`}>
              {course.category}
            </span>

            {course.status === 'Pending' && (
              <CoursePendingHint
                approverName={approvers[course.id]?.approverName}
                approverEmail={approvers[course.id]?.approverEmail}
              />
            )}

            <p className="trainer-coursemanagement-course-description">{course.description}</p>

            <div className="trainer-coursemanagement-course-instructor">by {course.instructor}</div>

            <div className="trainer-coursemanagement-course-stats">
              <div className="trainer-coursemanagement-stat-item">
                <Users className="trainer-coursemanagement-stat-icon-small" size={16} />
                <span>{course.students}</span>
              </div>
              <div className="trainer-coursemanagement-stat-item">
                <BookOpen className="trainer-coursemanagement-stat-icon-small" size={16} />
                <span>{course.modules} modules</span>
              </div>
            </div>

            <div className="trainer-coursemanagement-course-metrics">
              <div className="trainer-coursemanagement-metric">
                <Star className="trainer-coursemanagement-metric-icon" size={16} fill="#FACC15" color="#FACC15" />
                <span>{course.rating}</span>
              </div>
              <div className="trainer-coursemanagement-metric">
                <TrendingUp className="trainer-coursemanagement-metric-icon" size={16} color="#00A045" />
                <span>{course.completion}%</span>
              </div>
            </div>

            <div className="trainer-coursemanagement-course-actions">
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-view"
                onClick={() => handleView(course.id)}
              >
                <Eye size={16} /> View
              </button>
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-edit"
                onClick={() => handleEdit(course.id)}
              >
                <Edit size={16} /> Edit
              </button>
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-icon-btn"
                onClick={() => handleCopy(course.id)}
                title="Duplicate course"
              >
                <Copy size={16} />
              </button>
              <button
                className="trainer-coursemanagement-action-btn trainer-coursemanagement-action-icon-btn"
                onClick={() => handleDelete(course.id)}
                title="Delete course"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="trainer-coursemanagement-course-updated">Updated {course.updatedAt}</div>
          </div>
          ))}
        </div>
      )}
    </>
  );
};

export default CourseListTab;